import "./App.css";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { CssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Outlet } from "react-router-dom";
import { onError } from "@apollo/client/link/error";
import { ApolloLink } from "@apollo/client";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  experimental_extendTheme as extendMaterialTheme,
  THEME_ID,
} from "@mui/material/styles";
import { AppProvider } from "./contexts/AppContext";
import Notifications from "./x-components/Notifications";
import { accessTokenManager } from "./models/AccessTokenManager";

const materialTheme = extendMaterialTheme();

function createApolloClient(accessToken: string) {
  const httpLink = new HttpLink({
    uri: process.env.REACT_APP_GRAPHQL_URL as string,
  });

  const logoutLink = onError((err) => {
    if ((err.networkError as any)?.statusCode === 401) {
      window.location.href = "/auth/sign-in";
    }
  });

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers }: any) => ({
      headers: {
        authorization: `Bearer ${accessToken}`,
        ...headers,
      },
    }));
    return forward(operation);
  });

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: authLink.concat(logoutLink).concat(httpLink),
  });

  return apolloClient;
}

function invalidateAndRefetch(client: ApolloClient<NormalizedCacheObject>) {
  return (args: { typename: string; id: string | number }) => {
    const cacheId = client.cache.identify({
      __typename: args.typename,
      id: args.id,
    });
    client.cache.evict({ id: cacheId });
    client.cache.gc();

    client.refetchQueries({
      include: "active",
    });
  };
}

export default function App() {
  const accessToken = accessTokenManager.retrieve();
  if (!accessToken) return null;

  const apolloClient = createApolloClient(accessToken);
  return (
    <AppProvider
      onFragmentUpdate={invalidateAndRefetch(apolloClient)}
      accessToken={accessToken}
    >
      <ApolloProvider client={apolloClient}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={{
            calendarWeekNumberHeaderText: "#",
            calendarWeekNumberText: (weekNumber) => `${weekNumber}.`,
          }}
        >
          <MaterialCssVarsProvider
            modeStorageKey="qcsku.com"
            defaultMode="dark"
            theme={{ [THEME_ID]: materialTheme }}
          >
            <CssVarsProvider
              modeStorageKey="qcsku.com"
              defaultMode="dark"
              disableTransitionOnChange
            >
              <CssBaseline />
              <Outlet />
              <Notifications />
            </CssVarsProvider>
          </MaterialCssVarsProvider>
        </LocalizationProvider>
      </ApolloProvider>
    </AppProvider>
  );
}
